import {yupResolver} from '@hookform/resolvers/yup';
import {zodResolver} from '@hookform/resolvers/zod';
import i18next from 'i18next';
// eslint-disable-next-line no-restricted-imports -- Migrate to Zod
import * as Yup from 'yup';
import {z} from 'zod';

import {ReactNode, useCallback, useEffect, useId, useMemo} from 'react';
import {
  DefaultValues,
  FieldValues,
  FormProvider,
  KeepStateOptions,
  Path,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  UseFormReset,
  UseFormReturn,
  ValidationMode,
} from 'react-hook-form';

import {isNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {showNotification} from '../Notification/utils/events';
import {UnsavedChangesBlocker} from './components/UnsavedChangesBlocker';
import {useNavigateWithoutBlocker, NavigateWithoutBlocker} from './hooks/useNavigateWithoutBlocker';
import {FormControl, FormSubmitHandler, ValidationErrors} from './types';

export interface FormProps<TFieldValues extends FieldValues = FieldValues> {
  /**
   * @example
   * <Form>
   *  {(control) => (
   *    <FormField control={control} name="title" type="text" />
   *  )}
   * </Form>
   */
  children: (
    control: FormControl<TFieldValues>,
    formApi: UseFormReturn<TFieldValues>,
    navigateWithoutBlocker: NavigateWithoutBlocker
  ) => ReactNode;
  defaultValues?: DefaultValues<TFieldValues> | Nullish;
  values?: TFieldValues | Nullish;
  resetOptions?: KeepStateOptions;
  schema?: Yup.AnyObjectSchema;
  mode?: keyof ValidationMode;
  /**
   * @about onSubmit function has to be async and typed as FormSubmitHandler<YourFormValues>
   * @example
   * const onSubmit: FormSubmitHandler<LoginFormValues> = async (values) => {
   *   await loginToApp(...)
   * };
   */
  onSubmit?: FormSubmitHandler<TFieldValues>;
  onInvalidSubmit?: SubmitErrorHandler<TFieldValues>;
  onChange?: (
    values: TFieldValues,
    setErrors: (errors: ValidationErrors | null) => void,
    reset: UseFormReset<TFieldValues>,
    formApi: UseFormReturn<TFieldValues>
  ) => void;
  isFullHeight?: boolean;
  experimentalZodSchema?: z.ZodSchema<TFieldValues>;
  isDisabled?: boolean;
  /**
   * Enables a blocker dialog when user tries to navigate away from a form with unsaved changes.
   *
   * **IMPORTANT: If your onSubmit handler calls `navigate()`, you MUST check the `options.isSavingFromBlocker` flag
   * to prevent race conditions between the blocker's navigation and your handler's navigation.**
   *
   * @example
   * // ✅ CORRECT - Checking isSavingFromBlocker before navigating
   * const handleSubmit: FormSubmitHandler<FormType> = async (values, setErrors, reset, options) => {
   *   const isSavingFromBlocker = options?.isSavingFromBlocker ?? false;
   *
   *   await saveMutation(values)
   *     .unwrap()
   *     .then(() => {
   *       showNotification.success();
   *       // Only navigate if NOT saving from blocker (blocker will handle navigation)
   *       if (isSavingFromBlocker) {
   *          return;
   *        }
   *        navigate(settingsRoutes.advertisingQuickPresets);
   *     })
   *     .catch(handleApiError);
   * };
   *
   * @example
   * // ❌ WRONG - Navigating without checking the flag (causes race condition)
   * const handleSubmit: FormSubmitHandler<FormType> = async (values) => {
   *   await saveMutation(values)
   *     .unwrap()
   *     .then(() => {
   *       navigate(settingsRoutes.myRoute); // Race condition! Don't do this!
   *     });
   * };
   *
   * @example
   * // ✅ CORRECT - Form that stays on same page (no navigation needed)
   * const handleSubmit: FormSubmitHandler<FormType> = async (values, setErrors, reset) => {
   *   await saveMutation(values)
   *     .unwrap()
   *     .then(() => {
   *       showNotification.success();
   *       reset(undefined, {keepValues: true}); // Clears dirty state
   *       // No navigation - form stays open
   *     })
   *     .catch(handleApiError);
   * };
   */
  shouldWatchForUnsavedChanges?: boolean;
}

export function Form<TFieldValues extends FieldValues = FieldValues>(
  props: FormProps<TFieldValues>
) {
  const formId = useId();
  const onSubmitHandler = props.onSubmit;
  const onChangeHandler = props.onChange;

  // TODO: Temporary solution for handling resolvers.
  // https://carvago.atlassian.net/browse/T20-70580
  // This will be replaced once zod validation schemas will be implemented everywhere.
  const getResolver = () => {
    if (props.experimentalZodSchema) {
      return zodResolver(props.experimentalZodSchema);
    }
    if (props.schema) {
      return yupResolver(props.schema);
    }
    return undefined;
  };

  const formApi = useForm<TFieldValues>({
    mode: props.mode ?? 'onSubmit',
    resolver: getResolver(),
    defaultValues: props.defaultValues ?? undefined,
    values: props.values ?? undefined,
    resetOptions: props.resetOptions,
    disabled: props.isDisabled,
  });

  useEffect(() => {
    if (isNilOrEmpty(props.values)) {
      formApi.reset(undefined);
    }
  }, [props.values]);

  const {navigateWithoutBlocker, shouldNavigateWithoutBlockerRef} = useNavigateWithoutBlocker();

  const setErrors = useCallback(
    (validationErrors: ValidationErrors | null) => {
      validationErrors?.forEach((error) => {
        formApi.setError(error.name as Path<TFieldValues>, {message: error.message});
      });
    },
    [formApi]
  );

  const onSubmit: SubmitHandler<TFieldValues> = useCallback(
    async (values) => {
      await onSubmitHandler?.(values, setErrors, formApi.reset);
    },
    [onSubmitHandler, setErrors, formApi.reset]
  );

  /**
   * Programmatically submits the form and returns a success indicator.
   * Used by UnsavedChangesBlocker to save before navigation.
   *
   * @param options - Optional configuration for submission
   * @param options.isSavingFromBlocker - Whether this submission is triggered from the blocker dialog
   * @returns Promise<boolean> - true if validation passed (regardless of API result), false if validation failed.
   *
   * @note API errors do not prevent navigation - they only show toast notifications.
   * Only validation errors will keep the user on the form to fix issues.
   */
  const submitForm = useCallback(
    (options?: {isSavingFromBlocker?: boolean}): Promise<boolean> =>
      new Promise((resolve) => {
        formApi.handleSubmit(
          async (values) => {
            await onSubmitHandler?.(values, setErrors, formApi.reset, options);
            // Validation passed - allow navigation regardless of API result.
            // API errors are handled by the submit handler (shows toast notification).
            resolve(true);
          },
          () => {
            // Validation failed - errors already shown on form fields.
            resolve(false);
          }
        )();
      }),
    [formApi, onSubmitHandler, setErrors]
  );

  const onInvalidSubmit: SubmitErrorHandler<TFieldValues> = async (errors, event) => {
    await props.onInvalidSubmit?.(errors, event);

    if (i18next.exists('errors.invalid_submit')) {
      showNotification.warning(i18next.t('errors.invalid_submit'));
    }

    if (process.env.ENV_TYPE === 'dev' || process.env.ENV_TYPE === 'stage') {
      console.warn(
        'Form submit failed due to these errors: ',
        JSON.stringify(errors, undefined, 2)
      );
    }
  };

  const onChange = useCallback(() => {
    onChangeHandler?.(formApi.getValues(), setErrors, formApi.reset, formApi);
  }, [formApi, onChangeHandler, setErrors]);

  const control = useMemo(
    () => Object.assign(formApi.control, {formId, onChange, submitForm}),
    [formApi.control, formId, onChange, submitForm]
  );

  return (
    <FormProvider {...formApi}>
      <form
        css={props.isFullHeight ? {height: '100%'} : undefined}
        onSubmitCapture={(event) => {
          if (event.target instanceof HTMLElement && event.target.getAttribute('id') === formId) {
            formApi.handleSubmit(onSubmit, onInvalidSubmit)(event);
          }
        }}
        id={formId}
        noValidate
      >
        {props.children(control, formApi, navigateWithoutBlocker)}
      </form>

      <UnsavedChangesBlocker
        isDisabled={!props.shouldWatchForUnsavedChanges}
        shouldNavigateWithoutBlockerRef={shouldNavigateWithoutBlockerRef}
      />
    </FormProvider>
  );
}
